import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Store } from '@ngrx/store';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { map, shareReplay, combineLatestWith, tap } from 'rxjs/operators';

import { PriceFormComponent } from './price-form/price-form.component';
import { SelectedCardComponent } from '../shared/campaign/selected-card/selected-card.component';
import { CampaignSelectedBannerComponent } from '../shared/campaign/selected-banner/banner.component';
import { FooterComponent, DonationOrgInfo } from '../shared/footer/footer.component';

import {
  selectCanAcceptRecurring,
  selectSelectedCampaignVM,
  selectAllCampaigns,
  selectSelectedId,
} from '../../state/campaing/selectors';
import { DonationFlowUiActions } from '../../state/donation-flow';
import {
  selectDonationType,
  selectPriceDraft,
  selectShowCampaignsColumnAll,
} from '../../state/donation-flow/selectors';

import { PriceEnvelope } from '../../types/stripe-models';
import { CampaignsActions } from '../../state/campaing/actions';
import { Observable } from 'rxjs';
import { CampaignComponent } from '../campaign/campaign.component';
import { DonationType, PriceDraft } from '../../types/donation-flow.models';
import { RecentDonorsContainer } from '../donations-feed/recent-donors.container';
import { ModalLayoutService } from '../../services/modal-layout.service';
import { ModalNavigationService } from '../../services/modal-navigation.service';
import { TranslateModule } from '@ngx-translate/core';
import { selectUniqueDonorsCount } from '../../state/donations-feed/selectors';

@Component({
    selector: 'lib-donation',
    imports: [
        CommonModule,
        CampaignComponent,
        PriceFormComponent,
        SelectedCardComponent,
        CampaignSelectedBannerComponent,
        RecentDonorsContainer,
        TranslateModule,
        FooterComponent,
    ],
    templateUrl: './donation.component.html',
    styleUrls: ['./donation.component.scss']
})
export class DonationComponent implements OnInit {
  org: DonationOrgInfo = {
    legalName: 'Crezco Foundation Inc',
    ein: '921100986',
    email: 'info@crezcofoundation.org',
    verified: true,
  };

  private store = inject(Store);
  private modalNav = inject(ModalNavigationService);

  private layout = inject(ModalLayoutService);
  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.store.dispatch(CampaignsActions.load());

    // Si hay columna de campañas -> modal ancho; si no -> normal
    this.showCampaignsCol$
      .pipe(
        tap((show) => {
          if (show) {
            this.layout.setSize('donation', 'wide');
          } else {
            this.layout.clear('donation');
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  canAcceptRecurring$ = this.store.select(selectCanAcceptRecurring);

  selectedCampaign$ = this.store
    .select(selectSelectedCampaignVM)
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  selectedCampaignSig = toSignal(this.selectedCampaign$, {
    initialValue: null,
  });

  donorsCount$ = this.store.select(selectUniqueDonorsCount);

  draftPrice$ = this.store.select(selectPriceDraft).pipe(
    combineLatestWith(this.store.select(selectDonationType)),
    map(([price, donationType]) => ({ ...price, donationType }))
  );

  // mockCampaign = {
  //   nameEs: 'Campaña de prueba',
  //   nameEn: 'Test Campaign',
  //   descriptionEs: 'Esta es una descripción de prueba para validar cómo se ve la barra de progreso.',
  //   descriptionEn: 'This is a test description to validate the progress bar.',
  //   currentAmount: 148000,
  //   targetAmount: 150000
  // } as any;

  showCampaignsCol$ = this.store.select(selectShowCampaignsColumnAll);

  campaigns$ = this.store.select(selectAllCampaigns);
  selectedId$ = this.store.select(selectSelectedId);

  onSelectCampaign = (id: string) => {
    this.store.dispatch(CampaignsActions.select({ id, source: 'user' }));
  };

  onSubmit = (formValue: PriceEnvelope) => {
    const data = formValue?.data;

    // Detect recurring explicitly at the form boundary; from here on we only
    // pass around a `DonationType` (no boolean, no string literal).
    const isRecurring =
      !!data?.recurring &&
      typeof data.recurring === 'object' &&
      !!(data.recurring as any).interval;

    const donationType: DonationType = isRecurring
      ? DonationType.Recurring
      : DonationType.OneTime;

    // Snapshot of the currently selected campaign for product identifiers.
    const vm = this.selectedCampaignSig();
    const productId = vm?.productId ?? undefined;
    const productFeeId = vm?.productFeeId ?? undefined;

    const draft: PriceDraft = {
      action: 'Create',
      productId,
      productFeeId,
      currency: data?.currency,
      unitAmount:
        data?.unitAmount != null ? String(data.unitAmount) : undefined,
      nickname: data?.nickname,
      recurring: isRecurring ? data.recurring : undefined,
    };

    // `changeDonationType` triggers a centralized cleanup of anything that
    // belongs to the previous flow (SetupIntent, fees, invoice/subscription,
    // Payment Element slice). It's a no-op when the type didn't change.
    this.store.dispatch(
      DonationFlowUiActions.changeDonationType({ donationType })
    );
    this.store.dispatch(
      DonationFlowUiActions.setDonationType({ donationType })
    );
    this.store.dispatch(
      DonationFlowUiActions.setDeferFirstCharge({ isFirstChargeDeferred: false })
    );
    this.store.dispatch(
      DonationFlowUiActions.setFirstPaymentDate({ firstPaymentDate: null })
    );
    this.store.dispatch(DonationFlowUiActions.setPriceDraft({ draft }));

    this.modalNav.navigateToPaymentStep(donationType, 2);
  };
}
